import { Address, MarketMeta, makeUsd } from "@silo/types";
import { Array, BigInt, Effect, Option, pipe } from "effect";
import { getRawDashboardMarketAsset } from "./get-raw-dashboard-market-asset";
import { RawDashboardMarket } from "./types";
import { ContractsClient } from "../contracts-client";
import { getConfigIsDeprecated } from "@silo/config";

export type GetRawDashboardMarket = ReturnType<typeof getRawDashboardMarket>;
export type GetRawDashboardMarketSuccess =
  Effect.Effect.Success<GetRawDashboardMarket>;
export type GetRawDashboardMarketError =
  Effect.Effect.Error<GetRawDashboardMarket>;
export type GetRawDashboardMarketContext =
  Effect.Effect.Context<GetRawDashboardMarket>;

interface GetRawDashboardMarketParams {
  user: Address;
  marketMeta: MarketMeta;
}

export function getRawDashboardMarket({
  user,
  marketMeta,
}: GetRawDashboardMarketParams) {
  return Effect.gen(function* () {
    const { marketAddress, protocolKey } = marketMeta;

    const isDeprecated =
      marketMeta.isDeprecated ||
      getConfigIsDeprecated({ protocolKey, marketAddress });

    const marketAssetMetaArray = pipe(
      [marketMeta.baseAsset, ...marketMeta.bridgeAssets],
      Array.flatMapNullable((asset) => asset)
    );

    const [
      baseAsset,
      firstBridgeAsset,
      secondBridgeAsset,
      {
        userLtv,
        userLiquidationThreshold,
        healthFactor,
        borrowingPowerUsed,
        isAtRiskOfLiquidation,
      },
    ] = yield* Effect.all(
      [
        getRawDashboardMarketAsset({
          marketAssetMeta: marketMeta.baseAsset,
          user,
        }),
        getRawDashboardMarketAsset({
          marketAssetMeta: marketMeta.bridgeAssets[0],
          user,
        }),
        pipe(
          marketMeta.bridgeAssets[1],
          Option.fromNullable,
          Option.match({
            onNone: () => Effect.succeed(undefined),
            onSome: (marketAssetMeta) =>
              getRawDashboardMarketAsset({
                marketAssetMeta,
                user,
              }),
          })
        ),
        ContractsClient.getMarketUserState({
          marketAddress,
          user,
          ltArray: marketAssetMetaArray.map((asset) => asset.lt),
        }),
      ],
      { concurrency: "unbounded" }
    );

    const rawDashboardMarketAssets = pipe(
      [baseAsset, firstBridgeAsset, secondBridgeAsset],
      Array.flatMapNullable((asset) => asset)
    );

    const isPaused = rawDashboardMarketAssets.some((asset) => asset.isPaused);

    const depositBalanceUsd = pipe(
      rawDashboardMarketAssets,
      Array.map((asset) => asset.depositBalanceUsd),
      BigInt.sumAll,
      makeUsd
    );

    const debtBalanceUsd = pipe(
      rawDashboardMarketAssets,
      Array.map((asset) => asset.debtBalanceUsd),
      BigInt.sumAll,
      makeUsd
    );

    const dashboardMarket: RawDashboardMarket = {
      marketAddress,
      marketSymbol: baseAsset.symbol,
      isDeprecated,
      isPaused,

      userLtv,
      userLiquidationThreshold,
      healthFactor,
      borrowingPowerUsed,
      isAtRiskOfLiquidation,

      depositBalanceUsd,
      debtBalanceUsd,

      baseAsset,
      bridgeAssets: secondBridgeAsset
        ? [firstBridgeAsset, secondBridgeAsset]
        : [firstBridgeAsset],
    };

    return dashboardMarket;
  });
}
